import { useState, useCallback, useEffect } from 'react';
import { Table, TableHeader, TableBody, Column, Row } from 'react-aria-components';
import { useLocation, useParams } from 'react-router-dom';

import { useAtom } from 'jotai';

import { getPerformanceRecords } from '@/api/getPerformanceRecords';
import Breadcrumbs, { BreadcrumbItem } from '@/components/Breadcrumbs/Breadcrumbs';
import Button from '@/components/Button/Button';
import { CreateDialog } from '@/components/reservations/children/performance/CreateDialog';
import { PublishDialog } from '@/components/reservations/children/performance/PublishDialog';
import { ReasonDialog } from '@/components/reservations/children/performance/ReasonDialog';
import { NotificationAtom } from '@/jotai/jotai';
import {
  isPreviousMonthRecordExist,
  displayPerformanceRecordStatus,
  canPublishRecord,
} from '@/logics/performanceRecords/performanceRecords';
import { PerformanceRecord } from '@/types/performanceRecord';

export const PerformancesPage = () => {
  const params = useParams<{ id: string }>();
  const childID = Number(params.id);

  const [isOpenCreateDialog, setIsOpenCreateDialog] = useState(false);
  const [isOpenPublishDialog, setIsOpenPublishDialog] = useState(false);
  const [isOpenReasonDialog, setIsOpenReasonDialog] = useState(false);
  const [performanceRecords, setPerformanceRecords] = useState<PerformanceRecord[]>([]);
  const [selectedPerformanceRecord, setSelectedPerformanceRecord] = useState<
    PerformanceRecord | undefined
  >(undefined);

  const [_, setNotification] = useAtom(NotificationAtom);

  const { pathname: pathName } = useLocation();
  const breadcrumbs: BreadcrumbItem[] = [{ path: pathName, name: '実績記録票一覧' }];

  // 実績記録票一覧の取得
  const fetchPerformanceRecords = useCallback(async () => {
    try {
      const result = await getPerformanceRecords(childID);
      if (result.status === 200) {
        setPerformanceRecords(result.result);
      }
    } catch (error) {
      setNotification({
        headingLevel: 'h2',
        type: 'error',
        bannerStyle: 'standard',
        title: '実績記録の取得に失敗しました',
        message: '実績記録の取得に失敗しました。',
      });
    }
  }, [childID]);

  useEffect(() => {
    fetchPerformanceRecords();
  }, []);

  return (
    <div className="flex flex-col">
      <div className="flex flex-row">
        <Breadcrumbs item={breadcrumbs} />
      </div>
      <div className="mb-4 mt-5 flex flex-col items-start justify-between gap-5">
        <h1 className="text-2xl font-bold">実績記録票一覧</h1>
        <div className="flex-shrink-0">
          {!isPreviousMonthRecordExist(performanceRecords) ? (
            <Button
              type="submit"
              size="lg"
              variant="solid-fill"
              onClick={() => setIsOpenCreateDialog(true)}
            >
              新規作成
            </Button>
          ) : (
            <Button
              type="submit"
              size="lg"
              variant="disabled"
              disabled
            >
              前月分は作成済みです
            </Button>
          )}
        </div>
      </div>
      {isOpenCreateDialog && (
        <CreateDialog
          isOpen={isOpenCreateDialog}
          setIsOpen={setIsOpenCreateDialog}
          afterSubmit={() => {
            fetchPerformanceRecords();
          }}
          childID={childID}
        />
      )}

      <Table className="w-full border-collapse border border-gray-200">
        <TableHeader>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            年月
          </Column>
          <Column
            isRowHeader
            className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium"
          >
            ステータス
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            差し戻し理由
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            公開日
          </Column>
          <Column className="border border-gray-200 bg-gray-50 px-4 py-2 font-medium">
            公開設定
          </Column>
        </TableHeader>
        <TableBody>
          {performanceRecords.map((performanceRecord, index) => (
            <Row key={index}>
              <Column
                key="target_date"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {performanceRecord.target_date.substring(0, 7)}
              </Column>
              <Column
                key="status"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {displayPerformanceRecordStatus(performanceRecord.status)}
              </Column>
              <Column
                key="rejection_reason"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {performanceRecord.rejection_reason ? (
                  <Button
                    size="sm"
                    variant="text"
                    type="submit"
                    onClick={() => {
                      setSelectedPerformanceRecord(performanceRecord);
                      setIsOpenReasonDialog(true);
                    }}
                  >
                    確認する
                  </Button>
                ) : (
                  <span className="font-medium">なし</span>
                )}
              </Column>
              <Column
                key="published_at"
                className="border border-gray-200 px-4 py-2 font-medium"
              >
                {performanceRecord.published_at?.substring(0, 10)}
              </Column>
              <Column
                key="publish"
                className="border border-gray-200 px-4 py-2"
              >
                <Button
                  size="sm"
                  variant={canPublishRecord(performanceRecord.status) ? 'solid-fill' : 'disabled'}
                  type="submit"
                  disabled={!canPublishRecord(performanceRecord.status)}
                  onClick={() => {
                    setSelectedPerformanceRecord(performanceRecord);
                    setIsOpenPublishDialog(true);
                  }}
                >
                  公開する
                </Button>
              </Column>
            </Row>
          ))}
        </TableBody>
      </Table>
      {isOpenPublishDialog && (
        <PublishDialog
          isOpen={isOpenPublishDialog}
          setIsOpen={setIsOpenPublishDialog}
          afterSubmit={() => {
            fetchPerformanceRecords();
          }}
          performanceRecord={selectedPerformanceRecord!}
        />
      )}
      {isOpenReasonDialog && (
        <ReasonDialog
          isOpen={isOpenReasonDialog}
          setIsOpen={setIsOpenReasonDialog}
          performanceRecord={selectedPerformanceRecord!}
        />
      )}
    </div>
  );
};
